import type { AccessGroup, ServiceTeamRef } from "types/interfaces/access";
import {
  hiddenReason,
  hiddenText,
  marksOf,
  ruleOfGroup,
} from "components/userManagement/clientOwnershipModel";

/** Groups in picker order: the seeded order first, then by name. */
export function pickerOrder(groups: AccessGroup[]): AccessGroup[] {
  return [...groups].sort(
    (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
  );
}

export function groupName(
  groups: AccessGroup[] | undefined,
  id: number | null | undefined,
): string {
  if (id == null) return "No group";
  return groups?.find((g) => g.id === id)?.name ?? `Group #${id}`;
}

/** Why a member of `group` named on this client would not see it - their team
 * does not work it, or its demo mark is the wrong one for the group; null when
 * they would, or before a group is picked. */
export function assignedClientHidden(
  group: AccessGroup | null,
  client: { service_teams?: ServiceTeamRef[]; is_demo?: boolean } | undefined,
): string | null {
  if (!group || !client) return null;
  const rule = ruleOfGroup(group);
  const reason = hiddenReason(rule, marksOf(client));
  return reason ? hiddenText(reason, rule) : null;
}

/** What assigning clients means under `group`, for the Clients step. */
export function assignedClientsNote(group: AccessGroup | null): string | null {
  if (!group) return null;
  const scope = `${group.name} applies to: ${group.applies_to}.`;
  if (group.client_scope !== "assigned")
    return (
      `${scope} Assigned clients do not change what the user sees; they list ` +
      "the user as a Calibrate owner of the client."
    );
  if (group.demo_clients === "exclude")
    return `${scope} Demo clients stay out, assigned or not.`;
  if (group.demo_clients === "only")
    return `${scope} Only demo clients open, assigned or not.`;
  return null;
}
